import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, ShieldCheck, KeyRound } from 'lucide-react';
import { useAppState } from '../hooks/useAppState';
import { authApi, membersApi, type Member, type PassKey } from '../lib/api';
import { ICE, TEAL, ACCENT, FIELD, PANEL, PX_FONT, TITLE_FONT, ON_ACCENT, PAGE, pixelFrame } from '../components/guild/theme';

/* Admin: members, their roles, and the "verified veteran" fast-track (opens every gate; logged). */

const label = { fontFamily: PX_FONT, fontSize: 10, letterSpacing: '0.12em', color: TEAL } as const;
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never');
const ROLE_NAMES: Record<string, string> = { admin: 'Moderator', inner_circle: 'Inner Circle', accelerator: 'Accelerator', veteran: 'Verified veteran' };

export function MemberAdmin() {
  const navigate = useNavigate();
  const { user } = useAppState();
  const username = user?.discordUsername || 'trader';
  const [admin, setAdmin] = useState<boolean | null>(null);
  const [q, setQ] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [keys, setKeys] = useState<PassKey[]>([]);
  const loadKeys = () => membersApi.passKeys(username).then(setKeys).catch(() => undefined);
  useEffect(() => { if (admin) loadKeys(); }, [admin, username]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { authApi.me(username).then(me => setAdmin(me.roles.includes('admin'))).catch(() => setAdmin(false)); }, [username]);
  useEffect(() => {
    if (!admin) return;
    const id = setTimeout(() => membersApi.list(username, q).then(setMembers).catch(() => undefined), 250);
    return () => clearTimeout(id);
  }, [admin, username, q]);

  const toggle = async (m: Member) => {
    const on = !m.roles.includes('veteran');
    const updated = await membersApi.setVeteran(username, m.id, on);
    setMembers(ms => ms.map(x => (x.id === m.id ? { ...x, roles: updated.roles } : x)));
  };

  if (admin === false) return <div style={{ minHeight: '100vh', background: PAGE, padding: 40, color: ICE, fontFamily: PX_FONT }}>Members can only be managed by moderators.</div>;
  return (
    <div style={{ minHeight: '100vh', background: PAGE }}>
      <main style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 24px 64px', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <button type="button" onClick={() => navigate('/hub')} style={{ ...label, display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          <ArrowLeft size={12} /> Home Base
        </button>
        <h1 className="pal-title" style={{ margin: 0, fontFamily: TITLE_FONT, fontWeight: 900, fontSize: 40 }}>MEMBERS</h1>
        <div style={label}>Verified veterans skip Readiness Progression and the tier pick; use it for traders with a proven record. Every change is logged.</div>
        {/* Pass keys: guests who sign in without Discord */}
        <div style={{ background: PANEL, boxShadow: pixelFrame(), borderRadius: 'var(--pal-radius, 0)', padding: 8 }}>
          <div style={{ ...label, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}><KeyRound size={13} /> Pass keys</div>
          {keys.length === 0 && <div style={{ ...label, padding: '4px 12px 10px' }}>No pass keys issued.</div>}
          {keys.map(k => (
            <div key={k.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px', borderTop: '1px solid var(--pal-grid)', opacity: k.revoked ? 0.55 : 1 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: PX_FONT, fontWeight: 700, fontSize: 14, color: ICE }}>{k.name}{k.revoked && <span style={{ ...label, marginLeft: 8, color: '#FF8A8A' }}>Revoked</span>}</div>
                <div style={{ ...label, fontSize: 9 }}>
                  {k.roles.map(r => ROLE_NAMES[r] ?? r).join(' · ')} · {k.sign_ins} sign-in{k.sign_ins === 1 ? '' : 's'} · last used {when(k.last_used)} · issued {when(k.created_at)}
                </div>
              </div>
              <button type="button" onClick={async () => {
                if (!k.revoked && !window.confirm(`Revoke ${k.name}'s pass key? They're signed out and the key stops working.`)) return;
                await membersApi.revokePassKey(username, k.id, !k.revoked); loadKeys();
              }} style={{ fontFamily: PX_FONT, fontSize: 12, fontWeight: 700, padding: '7px 12px', border: 'none', borderRadius: 14, cursor: 'pointer', background: 'transparent', color: k.revoked ? ICE : '#FF8A8A', boxShadow: 'inset 0 0 0 1px var(--pal-rim)' }}>
                {k.revoked ? 'Restore' : 'Revoke'}
              </button>
            </div>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, background: FIELD, boxShadow: 'var(--pal-field-shadow)', borderRadius: 'var(--pal-radius, 0)', padding: '8px 12px' }}>
          <Search size={14} color={TEAL} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by Discord name"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: ICE, fontFamily: PX_FONT, fontSize: 13 }} />
        </label>
        <div style={{ background: PANEL, boxShadow: pixelFrame(), borderRadius: 'var(--pal-radius, 0)', padding: 8 }}>
          {members.length === 0 && <div style={{ ...label, padding: 12 }}>No members yet. They appear after their first Discord login.</div>}
          {members.map(m => {
            const vet = m.roles.includes('veteran');
            const staff = m.roles.includes('admin');
            return (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 12px', borderBottom: '1px solid var(--pal-grid)' }}>
                {m.avatar_url ? <img src={m.avatar_url} alt="" style={{ width: 32, height: 32, borderRadius: 16 }} /> : <span style={{ width: 32, height: 32, borderRadius: 16, background: FIELD }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: PX_FONT, fontWeight: 700, fontSize: 14, color: ICE }}>{m.username}</div>
                  <div style={{ ...label, fontSize: 9 }}>{m.via_pass_key ? 'Pass key · ' : ''}{m.roles.map(r => ROLE_NAMES[r] ?? r).join(' · ') || 'No roles'} · {m.trades} trades ({m.live_trades} live) · last signed in {when(m.last_sign_in)}</div>
                </div>
                {staff ? <span style={{ ...label, color: ACCENT }}>Unlocked as moderator</span> : (
                  <button type="button" onClick={() => toggle(m)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: PX_FONT, fontSize: 12, fontWeight: 700, padding: '7px 12px', border: 'none', borderRadius: 14, cursor: 'pointer',
                      background: vet ? ACCENT : 'transparent', color: vet ? ON_ACCENT : ICE, boxShadow: vet ? 'none' : 'inset 0 0 0 1px var(--pal-rim)' }}>
                    <ShieldCheck size={13} /> {vet ? 'Verified veteran' : 'Mark as veteran'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
